import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { companyInfo as companyFallback, heroSlides as heroFallback } from "@/mocks/site";
import { fleetVehicles } from "@/mocks/fleet";
import { services as servicesFallback } from "@/mocks/services";
import { galleryItems as galleryFallback } from "@/mocks/gallery";
import type {
  CompanyInfo,
  GaleriaRegistro,
  GalleryItem,
  HeroMedia,
  Service,
  Servicio,
  Vehiculo,
  Vehicle,
} from "@/types";

/** Loads active rows of a content table. Returns null while loading and [] on error. */
function useActiveRows<T>(table: string, orderColumn: string): T[] | null {
  const [rows, setRows] = useState<T[] | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from(table)
          .select("*")
          .eq("activo", true)
          .order(orderColumn, { ascending: true });
        if (error) throw error;
        if (active) setRows((data as T[]) ?? []);
      } catch {
        if (active) setRows([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [table, orderColumn]);

  return rows;
}

function toVehicle(vehiculo: Vehiculo): Vehicle {
  return {
    id: vehiculo.id,
    nombre: vehiculo.nombre,
    categoria: vehiculo.categoria,
    tipo: vehiculo.tipo,
    capacidad: vehiculo.capacidad,
    capacidadGrupo: vehiculo.capacidad_grupo,
    descripcion: vehiculo.descripcion,
    caracteristicas: vehiculo.caracteristicas ?? [],
    imagen_url: vehiculo.imagen_url,
    estado: vehiculo.estado,
    zona: vehiculo.zona,
    servicio: vehiculo.servicio,
    orden: vehiculo.orden,
    activo: vehiculo.activo,
  };
}

export function useCompanyInfo(): CompanyInfo {
  const [info, setInfo] = useState<CompanyInfo>(companyFallback as CompanyInfo);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("site_settings")
          .select("valor")
          .eq("clave", "company")
          .maybeSingle();
        if (error) throw error;
        const stored = (data?.valor ?? {}) as Partial<CompanyInfo>;
        if (active && stored && Object.keys(stored).length > 0) {
          setInfo({
            ...(companyFallback as CompanyInfo),
            ...stored,
            redes: stored.redes ?? (companyFallback as CompanyInfo).redes,
          });
        }
      } catch {
        /* keep fallback */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return info;
}

export function useHeroSlides() {
  const rows = useActiveRows<HeroMedia>("hero_media", "orden");
  if (!rows || rows.length === 0) {
    return heroFallback as { id: string; titulo: string; subtitulo: string; imagen_url: string }[];
  }
  return rows.map((row) => ({
    id: row.id,
    titulo: row.titulo,
    subtitulo: row.subtitulo,
    imagen_url: row.url,
  }));
}

export function useVehiculos(): Vehicle[] {
  const rows = useActiveRows<Vehiculo>("vehiculos", "orden");
  if (!rows || rows.length === 0) return fleetVehicles as Vehicle[];
  return rows.map(toVehicle);
}

export function useServicios(): Service[] {
  const rows = useActiveRows<Servicio>("servicios", "orden");
  if (!rows || rows.length === 0) return servicesFallback as Service[];
  return rows as unknown as Service[];
}

export function useGaleria(): GalleryItem[] {
  const rows = useActiveRows<GaleriaRegistro>("galeria", "orden");
  if (!rows || rows.length === 0) return galleryFallback as GalleryItem[];
  return rows.map((row) => ({
    id: row.id,
    titulo: row.titulo,
    descripcion: row.descripcion,
    categoria: row.categoria,
    imagen_url: row.imagen_url,
    fecha: row.fecha ?? undefined,
    orden: row.orden,
    activo: row.activo,
  }));
}